import { inject, Injectable } from '@angular/core';
import {
  CalendarEventType,
  CallendarEventPriority,
  CallendarEventStatus,
} from '@praceprofirmy/shared-domain';
import { Apollo } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  CalendarEventFragment,
  CreateCalendarEventDocument,
  CreateCalendarEventMutation,
  CreateCalendarEventMutationVariables,
  CreateCalendarEventParticipantDocument,
  CreateCalendarEventParticipantMutation,
  CreateCalendarEventParticipantMutationVariables,
  GetCalendarEventsDocument,
  GetCalendarEventsQuery,
  GetCalendarEventsQueryVariables,
  GetMyCalendarEventsDocument,
  GetMyCalendarEventsQuery,
  GetMyCalendarEventsQueryVariables,
  RemoveCalendarEventDocument,
  RemoveCalendarEventMutation,
  RemoveCalendarEventMutationVariables,
  RemoveCalendarEventParticipantDocument,
  RemoveCalendarEventParticipantMutation,
  RemoveCalendarEventParticipantMutationVariables,
  UpdateCalendarEventDocument,
  UpdateCalendarEventMutation,
  UpdateCalendarEventMutationVariables,
} from '../models';

export interface CreateCalendarEventInput {
  name: string;
  description?: string;
  startTime: Date;
  endTime: Date;
  status: CallendarEventStatus;
  priority: CallendarEventPriority;
  type: CalendarEventType;
  color?: string;
}

export interface UpdateCalendarEventInput {
  id: string;
  name?: string;
  description?: string;
  startTime?: Date;
  endTime?: Date;
  status?: CallendarEventStatus;
  priority?: CallendarEventPriority;
  type?: CalendarEventType;
  color?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CalendarEventGraphqlService {
  protected readonly apollo = inject(Apollo);

  /**
   * Načte všechny kalendářové události
   */
  getCalendarEvents(
    variables?: GetCalendarEventsQueryVariables
  ): Observable<CalendarEventFragment[]> {
    return this.apollo
      .watchQuery<GetCalendarEventsQuery, GetCalendarEventsQueryVariables>({
        query: GetCalendarEventsDocument,
        variables: variables || {},
        fetchPolicy: 'cache-and-network',
      })
      .valueChanges.pipe(map(result => result.data?.calendarEvents || []));
  }

  /**
   * Načte kalendářové události aktuálního uživatele
   */
  getMyCalendarEvents(
    variables?: GetMyCalendarEventsQueryVariables
  ): Observable<CalendarEventFragment[]> {
    return this.apollo
      .watchQuery<GetMyCalendarEventsQuery, GetMyCalendarEventsQueryVariables>({
        query: GetMyCalendarEventsDocument,
        variables: variables || {},
        fetchPolicy: 'cache-and-network',
      })
      .valueChanges.pipe(map(result => result.data?.myCalendarEvents || []));
  }

  /**
   * Vytvoří novou kalendářovou událost
   */
  createCalendarEvent(input: CreateCalendarEventInput): Observable<CalendarEventFragment> {
    return this.apollo
      .mutate<CreateCalendarEventMutation, CreateCalendarEventMutationVariables>({
        mutation: CreateCalendarEventDocument,
        variables: {
          createCalendarEventInput: {
            name: input.name,
            description: input.description,
            startTime: input.startTime.toISOString(),
            endTime: input.endTime.toISOString(),
            status: input.status,
            priority: input.priority,
            type: input.type,
            color: input.color,
          },
        },
        refetchQueries: ['GetCalendarEvents', 'GetMyCalendarEvents'],
      })
      .pipe(
        map(result => {
          if (!result.data?.createCalendarEvent) {
            throw new Error('Failed to create calendar event');
          }
          return result.data.createCalendarEvent;
        })
      );
  }

  /**
   * Aktualizuje existující kalendářovou událost
   */
  updateCalendarEvent(input: UpdateCalendarEventInput): Observable<CalendarEventFragment> {
    return this.apollo
      .mutate<UpdateCalendarEventMutation, UpdateCalendarEventMutationVariables>({
        mutation: UpdateCalendarEventDocument,
        variables: {
          updateCalendarEventInput: {
            id: input.id,
            name: input.name,
            description: input.description,
            startTime: input.startTime?.toISOString(),
            endTime: input.endTime?.toISOString(),
            status: input.status,
            priority: input.priority,
            type: input.type,
            color: input.color,
          },
        },
        refetchQueries: ['GetCalendarEvents', 'GetMyCalendarEvents'],
      })
      .pipe(
        map(result => {
          if (!result.data?.updateCalendarEvent) {
            throw new Error('Failed to update calendar event');
          }
          return result.data.updateCalendarEvent;
        })
      );
  }

  /**
   * Smaže kalendářovou událost
   */
  removeCalendarEvent(id: string): Observable<CalendarEventFragment> {
    return this.apollo
      .mutate<RemoveCalendarEventMutation, RemoveCalendarEventMutationVariables>({
        mutation: RemoveCalendarEventDocument,
        variables: { id },
        refetchQueries: ['GetCalendarEvents', 'GetMyCalendarEvents'],
      })
      .pipe(
        map(result => {
          if (!result.data?.removeCalendarEvent) {
            throw new Error('Failed to remove calendar event');
          }
          return result.data.removeCalendarEvent;
        })
      );
  }

  /**
   * Přidá účastníka k kalendářové události
   */
  addParticipant(calendarEventId: string, participantId: string): Observable<any> {
    return this.apollo
      .mutate<
        CreateCalendarEventParticipantMutation,
        CreateCalendarEventParticipantMutationVariables
      >({
        mutation: CreateCalendarEventParticipantDocument,
        variables: {
          createCalendarEventParticipantInput: {
            calendarEvent: calendarEventId,
            participantId: participantId,
          },
        },
        refetchQueries: ['GetCalendarEvents', 'GetMyCalendarEvents'],
      })
      .pipe(
        map(result => {
          if (!result.data?.createCalendarEventParticipant) {
            throw new Error('Failed to add participant');
          }
          return result.data.createCalendarEventParticipant;
        })
      );
  }

  /**
   * Odebere účastníka z kalendářové události
   */
  removeParticipant(participantId: string): Observable<any> {
    return this.apollo
      .mutate<
        RemoveCalendarEventParticipantMutation,
        RemoveCalendarEventParticipantMutationVariables
      >({
        mutation: RemoveCalendarEventParticipantDocument,
        variables: { id: participantId },
        refetchQueries: ['GetCalendarEvents', 'GetMyCalendarEvents'],
      })
      .pipe(
        map(result => {
          if (!result.data?.removeCalendarEventParticipant) {
            throw new Error('Failed to remove participant');
          }
          return result.data.removeCalendarEventParticipant;
        })
      );
  }

  /**
   * Aktualizuje účastníky kalendářové události
   * Odebere staré účastníky a přidá nové
   */
  async updateParticipants(calendarEventId: string, participantIds: string[]): Promise<void> {
    // Nejprve získáme současnou událost s účastníky
    const currentEvent = await this.apollo
      .query<GetCalendarEventsQuery, GetCalendarEventsQueryVariables>({
        query: GetCalendarEventsDocument,
        variables: {},
        fetchPolicy: 'network-only',
      })
      .pipe(map(result => result.data?.calendarEvents?.find(event => event.id === calendarEventId)))
      .toPromise();

    if (!currentEvent) {
      throw new Error('Calendar event not found');
    }

    // Získáme současné účastníky
    const currentParticipantIds = currentEvent.participants?.map(p => p.participantId) || [];

    // Najdeme účastníky k odstranění
    const participantsToRemove =
      currentEvent.participants?.filter(p => !participantIds.includes(p.participantId)) || [];

    // Najdeme účastníky k přidání
    const participantsToAdd = participantIds.filter(id => !currentParticipantIds.includes(id));

    // Odstraníme staré účastníky
    for (const participant of participantsToRemove) {
      await this.removeParticipant(participant.id).toPromise();
    }

    // Přidáme nové účastníky
    for (const participantId of participantsToAdd) {
      await this.addParticipant(calendarEventId, participantId).toPromise();
    }
  }
}
